import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CoinsIcon, ArrowRightIcon, ClockIcon, TrophyIcon, ShieldCheckIcon } from 'lucide-react';
import { getUserCredits } from '@/lib/api/credits';
import type { UserCredits } from '@/types/database.types';
import Button from '@/components/form/Button';
import CreditTransactionList from '@/components/CreditTransactionList';
import { toast } from 'react-hot-toast';
import { useAuth } from '@/context/AuthContext';

export default function UserCreditsWidget() {
  const navigate = useNavigate();
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTransactions, setShowTransactions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEarlyAdopter, setIsEarlyAdopter] = useState(true); // Early adopter status
  const [cashoutAmount, setCashoutAmount] = useState<number>(5);
  const { user } = useAuth();

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getUserCredits();
        
        // Log the data for debugging
        // console.log('UserCreditsWidget: Received credits data:', data);
        
        // Validate the data to ensure it contains valid numbers
        if (isNaN(data.balance) || isNaN(data.lifetime_earned) || isNaN(data.lifetime_withdrawn)) {
          console.error('Invalid credit values received:', data);
          setError('Invalid credit data received');
          // Set default values to prevent NaN display
          setCredits({
            balance: 0,
            lifetime_earned: 0,
            lifetime_withdrawn: 0,
            eligible_for_cashout: false,
            min_cashout_amount: 5.00
          });
        } else {
          setCredits(data);
          
          // Set default cashout amount to minimum or balance (whichever is lower)
          if (data.eligible_for_cashout) {
            setCashoutAmount(Math.min(data.balance, data.min_cashout_amount));
          }
        }
      } catch (error) {
        console.error('Error fetching credits:', error);
        setError('Failed to load credit information');
        // Set default values on error
        setCredits({
          balance: 0,
          lifetime_earned: 0,
          lifetime_withdrawn: 0,
          eligible_for_cashout: false,
          min_cashout_amount: 5.00
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCredits();
  }, []);

  const formatCurrency = (amount: number) => {
    // Ensure amount is a valid number
    const validAmount = isNaN(amount) ? 0 : amount;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(validAmount);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  if (!credits) {
    return null;
  }

  // Log the final values being displayed
  // console.log('UserCreditsWidget: Displaying credits:', {
  //   balance: credits.balance,
  //   lifetime_earned: credits.lifetime_earned,
  //   lifetime_withdrawn: credits.lifetime_withdrawn
  // });

  // Check if user has verified their age
  const isAgeVerified = user?.user_metadata?.age_verified === true;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 border border-yellow-200 bg-gradient-to-br from-yellow-50 to-white">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-yellow-800 flex items-center">
          <CoinsIcon className="h-5 w-5 mr-2 text-yellow-600" />
          Your Cash Balance
        </h3>
      </div>
      
      {error ? (
        <div className="text-sm text-red-600 mb-2">
          {error}. Please refresh the page or try again later.
        </div>
      ) : (
        <>
          <div className="text-2xl font-bold text-yellow-700 mb-2">
            {formatCurrency(credits.balance)}
          </div>
          
          <div className="flex items-center text-sm text-yellow-700 mb-3">
            <div className={`mr-2 h-2 w-2 rounded-full ${credits.eligible_for_cashout ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <span>
              {credits.eligible_for_cashout 
                ? `Eligible to cash out ${formatCurrency(credits.min_cashout_amount)}` 
                : `Cash out possible from ${formatCurrency(credits.min_cashout_amount)}`}
            </span>
          </div>
        </>
      )}
      
      {isEarlyAdopter && (
        <div className="bg-yellow-100 p-3 rounded-lg mb-3 border border-yellow-200">
          <div className="flex items-center text-yellow-800 font-medium mb-1">
            <TrophyIcon className="h-4 w-4 mr-1" />
            Early Adopter Benefits
          </div>
          <p className="text-xs text-yellow-700">
            As an early adopter, you receive a 25% activity score boost!
          </p>
        </div>
      )}
      
      {!isAgeVerified && (
        <div className="bg-yellow-100 p-3 rounded-lg mb-3 border border-yellow-200">
          <div className="flex items-center text-yellow-800 font-medium mb-1">
            <ShieldCheckIcon className="h-4 w-4 mr-1" />
            Age Verification Required
          </div>
          <p className="text-xs text-yellow-700 mb-2">
            You must verify your age to participate in CashSNAPs and withdraw earnings.
          </p>
          <Button
            onClick={() => navigate('/verify-age')}
            variant="primary"
            size="sm"
            className="w-full bg-yellow-600 hover:bg-yellow-700"
          >
            Verify Age
          </Button>
        </div>
      )}
      
      <div className="flex space-x-2">
        <Button
          onClick={() => navigate('/credits')}
          variant="primary"
          size="sm"
          className={`w-full ${credits.eligible_for_cashout ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-400 hover:bg-gray-500'}`}
          disabled={!credits.eligible_for_cashout || !isAgeVerified}
        >
          {credits.eligible_for_cashout ? 'Cash Out' : 'View Details'}
          <ArrowRightIcon className="h-4 w-4 ml-1" />
        </Button>
      </div>
      
      {!error && credits.lifetime_earned > 0 && (
        <div className="mt-3 text-xs text-gray-500">
          <div className="flex justify-between">
            <span>Lifetime earned:</span>
            <span className="font-medium text-green-600">{formatCurrency(credits.lifetime_earned)}</span>
          </div>
          {credits.lifetime_withdrawn > 0 && (
            <div className="flex justify-between">
              <span>Lifetime withdrawn:</span>
              <span className="font-medium text-blue-600">{formatCurrency(credits.lifetime_withdrawn)}</span>
            </div>
          )}
        </div>
      )}
      
      <div className="mt-4">
        <button
          onClick={() => setShowTransactions(!showTransactions)}
          className="text-sm text-yellow-700 hover:text-yellow-800 font-medium flex items-center"
        >
          {showTransactions ? 'Hide recent transactions' : 'Show recent transactions'}
          <span className="ml-1">{showTransactions ? '▲' : '▼'}</span>
        </button>
        
        {showTransactions && (
          <div className="mt-3">
            <CreditTransactionList limit={3} showPagination={false} />
          </div>
        )}
      </div>
    </div>
  );
}